import usePagination from '@/hooks/pagination';
import { useEffect, useState } from 'react';
import { createCompany, deleteCompany, updateCompany, type Company } from '@/api/company.service';
import { getCompaniesByClient } from '@/api/client.service'
import { CrudPage } from '@/components/crud/CrudPage';
import { CompaniesForm } from './CompaniesForm';
import { useParams } from "react-router";
import { getClient, type Client } from '@/api/client.service';
import { Button, IconButton, Stack, TextField } from '@mui/material';
import { Add, Edit, Delete } from "@mui/icons-material"
import { FilterModal } from '@/components/FilterModal';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useNotification } from '@/context/NotificationContext';
import { useLogger } from '@/hooks/useLogger';
import { LogEventType } from '@/api/log.service';
import { formatCNPJ } from '@/utils/cnpjUtils';
import { DataTable } from '@/components/DataTable';
import { CrudModal } from '@/components/crud/CrudModal';
import { useCrud } from '@/hooks/useCrud';

type CompanyFilter = {
  clientId?: string;
  cnpj?: string;
  erpCode?: string;
  name?: string;
  socialReason?: string;
  active?: boolean;
};

export const CompaniesPage = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { notify } = useNotification();
  const { log } = useLogger();

  // Filters
  const [inputValues, setInputValues] = useState({ name: "", cnpj: "", erpCode: "", socialReason: "" });
  const [filters, setFilters] = useState<CompanyFilter>({});

  // Logic Hooks
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Company>();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Data State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [client, setClient] = useState<Client>();
  const [isLoading, setIsLoading] = useState(true);

  const loadData = async () => {
    if (!clientId) return;

    // setIsLoading(true);
    try {
      const [companiesData, clientData] = await Promise.all([
        getCompaniesByClient({
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          clientId: clientId,
          ...filters
        }),
        getClient(clientId)
      ]);
      setCompanies(companiesData.companies ?? []);
      setTotal(companiesData.total ?? 0);
      setClient(clientData);
    } catch (error) {
      notify("Falha ao carregar dados", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [page, rowsPerPage, clientId, filters]);

  // 3. Handlers
  const onSave = async (values: Partial<Company>) => {
    // Validate required fields
    if (!values.name?.trim()) {
      notify("Nome é obrigatório", "error");
      return;
    }

    if (!values.cnpj?.trim()) {
      notify("CNPJ é obrigatório", "error");
      return;
    }
    else if (values.cnpj.replace(/\D/g, "").length < 14) {
      notify("CNPJ deve ter 14 números", "error");
      return;
    }

    if (!values.social_reason?.trim()) {
      notify("Razão Social é obrigatória", "error");
      return;
    }

    try {
      let result: Company;
      if (editing) {
        result = await updateCompany(editing.id, values);
        log(LogEventType.Updated, result, "company", result.id, "info");
      } else {
        result = await createCompany({ ...values, client_id: clientId! } as Company);
        log(LogEventType.Created, result, "company", result.id, "info");
      }
      notify(`Empresa ${editing ? "atualizada" : "criada"} com sucesso`, "success");
      loadData();
      handleClose();
    } catch (e) {
      notify("Erro ao processar operação", "error");
    }
  };

  const onDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      const company = companies.find(c => c.id === deleteId);
      await deleteCompany(deleteId);
      log(LogEventType.Deleted, company ?? { id: deleteId }, "company", deleteId, "info");
      notify("Empresa deletada com sucesso", "success");
      loadData();
    } finally {
      setDeleteId(null);
    }
  };

  const handleInputChange = (field: keyof typeof inputValues, value: string) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setFilters({
      name: inputValues.name || undefined,
      cnpj: inputValues.cnpj || undefined,
      erpCode: inputValues.erpCode || undefined,
      socialReason: inputValues.socialReason || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    const empty = { name: "", cnpj: "", erpCode: "", socialReason: "" };
    setInputValues(empty);
    setFilters({});
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (isLoading) return <div>Carregando empresas...</div>;

  return (
    <CrudPage
      title={`Empresas de ${client?.name || '...'}`}
      subtitle="Gerencie as empresas vinculadas a este cliente"
      headerActions={
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate} >
          Nova Empresa
        </Button>
      }
    >
      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Nome"
          size="small"
          fullWidth
          value={inputValues.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
        />
        <TextField
          label="Razão Social"
          size="small"
          fullWidth
          value={inputValues.socialReason}
          onChange={(e) => handleInputChange("socialReason", e.target.value)}
        />
        <TextField
          label="CNPJ"
          size="small"
          fullWidth
          value={inputValues.cnpj}
          onChange={(e) => handleInputChange("cnpj", e.target.value)}
        />
        <TextField
          label="Código ERP"
          size="small"
          fullWidth
          value={inputValues.erpCode}
          onChange={(e) => handleInputChange("erpCode", e.target.value)}
        />
      </FilterModal>
      <DataTable
        data={companies}
        columns={[
          { key: "name", label: "Nome" },
          { key: "social_reason", label: "Razão Social" },
          { key: "cnpj", label: "CNPJ", render: (row) => formatCNPJ(row.cnpj) },
          { key: "erp_code", label: "Código ERP" },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(row) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton onClick={() => handleEdit(row)} color="primary">
              <Edit />
            </IconButton>
            <IconButton onClick={() => setDeleteId(row.id)} color="error">
              <Delete />
            </IconButton>
          </Stack>
        )}
      />

      <CrudModal
        open={open}
        onClose={handleClose}
        title={editing ? "Editar Empresa" : "Nova Empresa"}
        initialData={editing}
        onSubmit={onSave}
        Form={CompaniesForm}
      />

      <ConfirmModal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={onDeleteConfirm}
        title="Exclusão de empresa"
        text={`Tem certeza que deseja excluir ${companies.find(c => c.id === deleteId)?.name ?? "o registro"}?`}
        btnText="Confirmar"
      />
    </CrudPage>
  );
};